import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip, 
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField,
  Badge,
} from '@mui/material';
import {
  Close,
  DragIndicator,
  Clear,
  PlayArrow,
  ContentCopy,
  Link as LinkIcon,
  Code,
  ViewList,
  Save,
  Delete,
  Star,
  StarBorder,
  Sort as SortIcon,
} from '@mui/icons-material';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { QueryBuilder } from './query-builder';
import type { LogisticsObjectScope } from '@/types/logisticsObject';
import { QueryExpressionFromJSON, SearchLogisticsObjectsRequest } from '@/api/bol';
import { useTheme, useSavedQueries } from '@/hooks';
import { SaveQueryDialog } from './SaveQueryDialog';
import { SavedQuery } from '@/types/savedQuery';

interface SearchFormData {
  searchQuery: SearchLogisticsObjectsRequest;
  selectedQuery: SavedQuery | null;
}

interface SearchPanelProps {
  title: string;
  onClose: () => void;
  onSearch: (searchData: unknown) => void;
  onReset: () => void;
  onCopyLink?: () => Promise<void> | void;
  loading?: boolean;
  logisticsObjectType?: LogisticsObjectScope;
  currentSearchQuery?: SearchLogisticsObjectsRequest;
}

const sortObjectKeysRecursively = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortObjectKeysRecursively);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort((a, b) => a.localeCompare(b))
      .reduce<Record<string, unknown>>((accumulator, key) => {
        accumulator[key] = sortObjectKeysRecursively((value as Record<string, unknown>)[key]);
        return accumulator;
      }, {});
  }

  return value;
};

const toComparableQueryString = (query: SearchLogisticsObjectsRequest | undefined): string => {
  const normalizedQuery = query && typeof query === 'object' && !Array.isArray(query)
    ? query
    : {};

  return JSON.stringify(sortObjectKeysRecursively(normalizedQuery));
};

const SearchPanel: React.FC<SearchPanelProps> = ({ 
  title, 
  onClose, 
  onSearch,
  onReset,
  onCopyLink,
  loading = false,
  logisticsObjectType = 'all',
  currentSearchQuery
}) => {
  const { mode } = useTheme();
  const {
    savedQueries,
    saveQuery: saveQueryToStorage,
    deleteQuery,
    setDefaultQuery,
  } = useSavedQueries(logisticsObjectType);
  const [viewMode, setViewMode] = useState<'visual' | 'json' | 'sort'>('visual');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [inputValue, setInputValue] = React.useState('');
  
  // React Hook Form setup
  const { control, handleSubmit, setValue, reset } = useForm<SearchFormData>({
    defaultValues: { searchQuery: {}, selectedQuery: null },
    mode: 'onChange'
  });

  const watchedSearchQuery = useWatch({ control, name: 'searchQuery' });
  const searchQuery = useMemo(() => watchedSearchQuery ?? {}, [watchedSearchQuery]);

  // Restore form state from currentSearchQuery when available
  useEffect(() => {
    if (currentSearchQuery !== undefined) {
      setValue('searchQuery', currentSearchQuery);
    }
  }, [currentSearchQuery, setValue]);
  
  // Panel width with localStorage persistence
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem('searchPanelWidth');
    return saved ? Number.parseInt(saved, 10) : 400;
  });

  const [isResizing, setIsResizing] = useState(false);

  // Constants
  const MIN_WIDTH = 300;
  const MAX_WIDTH = 800;

  // Persist panel width
  useEffect(() => {
    localStorage.setItem('searchPanelWidth', panelWidth.toString());
  }, [panelWidth]);

  // Copy query to clipboard
  const copySearchToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(searchQuery, null, 2));
    } catch (err) {
      console.error('Failed to copy search query to clipboard:', err);
    }
  }, [searchQuery]);

  const handleCopyLink = useCallback(async () => {
    if (!onCopyLink) {
      return;
    }
    await onCopyLink();
  }, [onCopyLink]);

  // Resize handlers
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    const constrainedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, e.clientX));
    setPanelWidth(constrainedWidth);
  }, [isResizing]);

  const handleMouseUp = useCallback(() => setIsResizing(false), []);

  // Setup resize event listeners
  useEffect(() => {
    if (!isResizing) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Form handlers
  const onSubmit = useCallback((data: SearchFormData) => {
    onSearch?.(data.searchQuery);
  }, [onSearch]);

  const handleReset = useCallback(() => {
    reset({
      searchQuery: currentSearchQuery ?? {},
      selectedQuery: null,
    });
    setInputValue('');
    onReset();
  }, [reset, currentSearchQuery, onReset]);

  // Query selection and loading
  const handleQuerySelect = useCallback((query: SavedQuery | null) => {
    setValue('selectedQuery', query);
    setInputValue(query?.name || '');
    if (!query?.query) return;

    const queryValue = query.query;
    const convertedQuery = queryValue?.query 
      ? { ...queryValue, query: QueryExpressionFromJSON(queryValue.query) }
      : queryValue;
    
    setValue('searchQuery', convertedQuery);
  }, [setValue]);

  // Save query handler with state refresh
  const handleSaveQuery = useCallback((queryName: string, isDefault?: boolean) => {
    if (isDefault) {
      saveQueryToStorage(queryName, searchQuery, true);
      return;
    }

    saveQueryToStorage(queryName, searchQuery);
  }, [saveQueryToStorage, searchQuery]);

  const handleToggleDefaultQuery = useCallback((queryId: string, isDefault: boolean, event: React.MouseEvent) => {
    event.stopPropagation();
    setDefaultQuery(queryId, !isDefault);
  }, [setDefaultQuery]);

  // Delete query handler with selection cleanup
  const handleDeleteQuery = useCallback((queryId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (deleteQuery(queryId)) {
      // Clear selection if deleted query was selected
      const currentSelection = control._formValues.selectedQuery;
      if (currentSelection?.id === queryId) {
        setValue('selectedQuery', null);
      }
    }
  }, [deleteQuery, control._formValues, setValue]);

  // Validate query
  const isValidQuery = useMemo(() => {
    return !!searchQuery && typeof searchQuery === 'object' && !Array.isArray(searchQuery);
  }, [searchQuery]);

  const isSameAsAppliedQuery = useMemo(() => {
    return toComparableQueryString(searchQuery) === toComparableQueryString(currentSearchQuery);
  }, [searchQuery, currentSearchQuery]);

  const isSearchDisabled = useMemo(() => {
    return loading || !isValidQuery || isSameAsAppliedQuery;
  }, [loading, isValidQuery, isSameAsAppliedQuery]);

  const searchTooltipTitle = useMemo(() => {
    if (!isValidQuery) {
      return 'Please build a valid query';
    }

    if (isSameAsAppliedQuery) {
      return 'Current query is already applied';
    }

    return 'Execute search with current query';
  }, [isValidQuery, isSameAsAppliedQuery]);

  // Check if query has content
  const hasQueryContent = useMemo(() => {
    return searchQuery?.query && 
           typeof searchQuery.query === 'object' && 
           Object.keys(searchQuery.query).length > 0;
  }, [searchQuery]);

  // Check if sort has content
  const hasSortContent = useMemo(() => {
    return searchQuery?.sort && 
           Array.isArray(searchQuery.sort) && 
           searchQuery.sort.length > 0;
  }, [searchQuery]);

  return (
    <Paper
      sx={{
        width: panelWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        m: 0,
        position: 'relative',
      }}
    >
      {/* Resize Handle */}
      <Box
        onMouseDown={(e) => {
          e.preventDefault();
          setIsResizing(true);
        }}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'col-resize',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'primary.main',
            opacity: 0.1,
          },
          transition: 'background-color 0.2s ease',
        }}
      >
        <DragIndicator 
          sx={{ 
            fontSize: 16, 
            color: 'grey.400',
            transform: 'rotate(90deg)',
            opacity: 0.7,
          }} 
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64, // Fixed height to match page title section
          px: 2,
          pr: 3, // Extra padding to account for resize handle
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: mode === 'dark' ? 'background.paper' : 'grey.50',
        }}
      >
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Copy search query to clipboard">
            <IconButton onClick={copySearchToClipboard} size="small" aria-label="Copy search query">
              <ContentCopy />
            </IconButton>
          </Tooltip>
          {onCopyLink && (
            <Tooltip title="Copy page link with current filters">
              <IconButton onClick={() => void handleCopyLink()} size="small" aria-label="Share link">
                <LinkIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small" aria-label="Close search panel">
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, pr: 1, minHeight: 0 }}>
        {/* Saved Queries Selector and View Toggle */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
              <Controller
                name="selectedQuery"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={savedQueries}
                    value={field.value}
                    inputValue={inputValue}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option.name;
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    onChange={(_, newValue) => {
                      if (typeof newValue === 'string') return;
                      handleQuerySelect(newValue);
                      setInputValue(newValue?.name || '');
                    }}
                    onInputChange={(_, newValue) => {
                      setInputValue(newValue);
                    }}
                    onBlur={() => {
                      // Clear input when blurred if no query is selected
                      if (!field.value) {
                        setInputValue('');
                      }
                    }}
                    size="small"
                    slotProps={{
                      ...{
                        paper: {
                          sx: {
                            width: 'max-content',
                            minWidth: '100%',
                            whiteSpace: 'normal',
                          }
                        }
                      },
                      clearIndicator: {
                        sx: {
                          visibility: 'visible',
                          padding: '4px',
                          borderRadius: '50%',
                          '& svg': {
                            display: 'block'
                          }
                        }
                      },
                      popupIndicator: {
                        sx: {
                          display: field.value ? 'none' : 'block',
                          padding: '2px',
                          borderRadius: '50%',
                          '& svg': {
                            display: 'block'
                          }
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="My Saved Queries"
                        placeholder="Select a saved query"
                        sx={{
                          '& .MuiInputBase-root': {
                            paddingRight: '35px !important', // Reduce right padding to give more space to text
                          },
                          '& .MuiAutocomplete-endAdornment': {
                            right: '4px', // Position icons closer to the edge
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      if (typeof option === 'string') return null;
                      return (
                        <li {...props}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="body2" sx={{ flex: 1, mr: 1 }}>
                              {option.name}
                            </Typography>
                            <Tooltip title={option.isDefault ? 'Unset default query' : 'Set as default query'}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleToggleDefaultQuery(option.id, !!option.isDefault, e)}
                                sx={{ flexShrink: 0 }}
                              >
                                {option.isDefault ? <Star fontSize="small" color="warning" /> : <StarBorder fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteQuery(option.id, e)}
                              sx={{ flexShrink: 0 }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </li>
                      );
                    }}
                  />
                )}
              />
            </Box>
            
            <Tooltip title="Save current query">
              <span>
                <Button
                  onClick={() => setSaveDialogOpen(true)}
                  disabled={!isValidQuery || loading}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    minWidth: 40,
                    height: 40,
                    px: 1
                  }}
                >
                  <Save />
                </Button>
              </span>
            </Tooltip>
            
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{ 
                flexShrink: 0,
                height: 40, // Match small Select height
                '& .MuiToggleButton-root': {
                  height: 40,
                  px: 1.5
                }
              }}
            >
              <ToggleButton value="visual">
                <Tooltip title="Query Builder">
                  <Badge 
                    color="secondary" 
                    variant="dot" 
                    invisible={!hasQueryContent}
                    sx={{
                      '& .MuiBadge-dot': {
                        top: 2,
                        right: 2,
                      }
                    }}
                  >
                    <ViewList fontSize="small" />
                  </Badge>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="sort">
                <Tooltip title="Sort Builder">
                  <Badge 
                    color="secondary" 
                    variant="dot" 
                    invisible={!hasSortContent}
                    sx={{
                      '& .MuiBadge-dot': {
                        top: 2,
                        right: 2,
                      }
                    }}
                  >
                    <SortIcon fontSize="small" />
                  </Badge>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="json">
                <Tooltip title="JSON Editor">
                  <Code fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Query Builder */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Controller
            name="searchQuery"
            control={control}
            render={({ field }) => (
              <QueryBuilder
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  setValue('selectedQuery', null); // Clear query selection when manually editing
                }}
                disabled={loading}
                mode={viewMode}
                logisticsObjectType={logisticsObjectType}
              />
            )}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
            <Tooltip title={searchTooltipTitle}>
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  startIcon={loading ? undefined : <PlayArrow />}
                  disabled={isSearchDisabled}
                  size="small"
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              </span>
            </Tooltip>
            <Button
              type="button"
              variant="outlined"
              fullWidth
              startIcon={<Clear />}
              onClick={handleReset}
              disabled={loading}
              size="small"
            >
              Reset to default
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Save Query Dialog */}
      <SaveQueryDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSubmit={handleSaveQuery}
      />
    </Paper>
  );
};

export default SearchPanel;